import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Close, SettingsInputAntenna } from "@mui/icons-material";
export const readerDialogPaper = {
  borderRadius: 2.5,
  overflow: "hidden",
  maxHeight: "92vh",
  m: { xs: 1, sm: 3 },
  width: { xs: "calc(100% - 16px)", sm: "calc(100% - 48px)" },
};
export function ReaderDialogHeader({ title, subtitle, onClose, busy = false }) {
  return (
    <DialogTitle sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: "#163c52", color: "#fff" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box sx={{ display: "flex", p: 1, bgcolor: "#ffffff15", borderRadius: 1.5 }}>
            <SettingsInputAntenna />
          </Box>
          <Box>
            <Typography
              component="span"
              display="block"
              variant="h6"
              color="inherit"
              fontWeight={700}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                component="span"
                display="block"
                variant="body2"
                sx={{ color: "#d6e4ec", mt: 0.25 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        <IconButton
          aria-label={`Cerrar ${title}`}
          disabled={busy}
          onClick={onClose}
          sx={{ color: "#fff", bgcolor: "#ffffff12" }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Stack>
    </DialogTitle>
  );
}
ReaderDialogHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};
export default function DeviceActionDialog({
  open,
  onClose,
  onConfirm,
  title,
  subtitle,
  actionLabel,
  fields = [],
  children,
  disabled = false,
  loading = false,
  progress,
  renderResult,
  maxWidth = "sm",
  fieldColumns = 6,
}) {
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const locked = useRef(false);
  const revision = useRef(0);
  useEffect(() => {
    ++revision.current;
    locked.current = false;
    setPending(false);
    setResult(null);
    setError("");
    return () => {
      ++revision.current;
    };
  }, [open]);
  const busy = pending || loading;
  const close = () => {
    if (!busy && !locked.current) onClose();
  };
  const confirm = async () => {
    if (locked.current || busy || disabled || result) return;
    locked.current = true;
    const id = revision.current;
    setPending(true);
    setError("");
    try {
      const response = await onConfirm();
      if (id !== revision.current) return;
      if (!response?.success && !response?.stats)
        throw new Error(
          response?.error || response?.message || "El servidor no confirmó la operación."
        );
      setResult(response);
    } catch (failure) {
      if (id === revision.current) setError(failure.message || "No se pudo ejecutar la operación.");
    } finally {
      if (id === revision.current) {
        locked.current = false;
        setPending(false);
      }
    }
  };
  const accepted =
    result?.isBackground ||
    ["processing", "timeout_but_processing", "accepted"].includes(result?.status);
  return (
    <Dialog
      open={open}
      onClose={close}
      disableEscapeKeyDown={busy}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{ sx: readerDialogPaper }}
      aria-label={title}
    >
      <ReaderDialogHeader title={title} subtitle={subtitle} onClose={close} busy={busy} />
      <DialogContent dividers sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: "#f7f9fc" }}>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {fields.map(([label, value]) => (
            <Grid item xs={12} sm={fieldColumns} key={label}>
              <Box sx={{ p: 1.5, border: "1px solid #dfe6ed", bgcolor: "#fff", borderRadius: 1.5 }}>
                <Typography variant="caption" sx={{ color: "#526579" }}>
                  {label}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: "#243b53", overflowWrap: "anywhere" }}
                >
                  {value ?? "Sin información"}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {busy && (
          <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }}>
            <Typography variant="body2">Esperando la respuesta de la operación…</Typography>
            {progress?.total > 0 && (
              <Typography variant="caption" display="block">
                {progress.current} de {progress.total} procesados · {progress.successCount || 0}{" "}
                correctos · {progress.failedCount || 0} fallidos
              </Typography>
            )}
          </Alert>
        )}
        {result ? (
          <Alert
            severity={
              accepted
                ? "info"
                : Number(
                    result.failedCount || result.stats?.failedCount || result.stats?.failed || 0
                  ) > 0
                ? "warning"
                : "success"
            }
          >
            <Typography variant="body2" fontWeight={600}>
              {accepted
                ? "Solicitud aceptada; ejecución pendiente de confirmar"
                : "Respuesta recibida del servidor"}
            </Typography>
            <Typography variant="body2">
              {result.message || "El servidor confirmó la solicitud."}
            </Typography>
            {(result.stats || result.successCount !== undefined) && (
              <Typography variant="caption" display="block">
                Correctos:{" "}
                {result.successCount ?? result.stats?.successCount ?? result.stats?.success ?? "—"}{" "}
                · Fallidos:{" "}
                {result.failedCount ?? result.stats?.failedCount ?? result.stats?.failed ?? "—"}
              </Typography>
            )}
            {renderResult?.(result)}
          </Alert>
        ) : (
          <Box component="fieldset" disabled={busy} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
            {children}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button disabled={busy} onClick={close} color="secondary">
          {result ? "Cerrar" : "Cancelar"}
        </Button>
        {!result && (
          <Button
            variant="contained"
            disabled={busy || disabled}
            onClick={confirm}
            sx={{ bgcolor: "#164b65", color: "#fff", "&:hover": { bgcolor: "#103b50" } }}
          >
            {busy ? "Procesando…" : error ? "Reintentar" : actionLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
DeviceActionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  actionLabel: PropTypes.string.isRequired,
  fields: PropTypes.array,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  progress: PropTypes.object,
  renderResult: PropTypes.func,
  maxWidth: PropTypes.string,
  fieldColumns: PropTypes.number,
};
